import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import i18next from "i18next";

import { IToken } from "../@types/Login";
import {
  AUTH_API_BASE_URL,
  AUTH_TOKEN_STORAGE,
} from "../constants/localStorageKeys";
import { readRequestedLanguage } from "../utils/requestedLanguage";
import { safeStorage, safeJsonParse } from "../utils/storage";
import { authService } from "./authService";

// Marks a request as anonymous (see ANONYMOUS_REQUEST): the interceptors skip the
// stored bearer token and the 401 refresh/redirect handling for it.
declare module "axios" {
  // Augmenting axios' own type; its name is not ours to prefix.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export interface AxiosRequestConfig {
    anonymous?: boolean;
  }
}

/**
 * The platform's primary (EU) API. Signed-in calls may be re-pointed at a
 * regional API by setApiBaseUrlForRegion; anonymous, pre-login flows that
 * have no region context must use this one explicitly (EN-5229).
 */
export const DEFAULT_API_URL = process.env.REACT_APP_API_URL;

/**
 * Per-request override for anonymous, pre-login calls (EN-5229): the OAuth and
 * invitation callback pages (Dexcom, Fitbit, Google Health, JupyterHealth
 * Exchange). The shared instance's baseURL is whatever the last sign-in stored
 * under `apiBaseUrl` — for a US-region user that is the US API — and a
 * callback has no region to honour, so it must always post to the default API.
 * Pass as the axios config argument: `api.post(url, body, ANONYMOUS_REQUEST)`.
 */
export const ANONYMOUS_REQUEST: AxiosRequestConfig = {
  baseURL: DEFAULT_API_URL,
  anonymous: true,
};

/**
 * Request interceptor body, exported for tests. Attaches the stored bearer token
 * unless the caller set one already or the request is anonymous: a staff browser
 * pinned to the US API must not send its US token to the EU API on a
 * patient-facing callback page (and if that ever 401'd, the response interceptor
 * would clear storage and redirect the patient to /login).
 */
export function attachStoredToken<T extends InternalAxiosRequestConfig>(
  request: T
): T {
  if (request.anonymous) {
    if (request.headers) delete request.headers.Authorization;
    return request;
  }

  if (request.headers?.Authorization) {
    return request;
  }

  const tokenResponse = safeStorage.getItem(AUTH_TOKEN_STORAGE);

  if (tokenResponse) {
    const parsedTokenResponse = safeJsonParse<IToken>(
      tokenResponse,
      {} as IToken
    );

    if (request.headers && parsedTokenResponse.idToken) {
      request.headers.Authorization = `${parsedTokenResponse.tokenType} ${parsedTokenResponse.idToken}`;
    }
  }

  return request;
}

/**
 * Request interceptor body, exported for tests. Tells the API which language to answer in.
 *
 * `ServiceContext.GetLanguageCodeAsync` reads Accept-Language first and only falls back to the
 * stored user preference when that header is absent — and browsers set Accept-Language on every
 * XHR by default. So without this the browser's own setting silently beat the language the staff
 * member picked in the portal, on every request (EN-5191). Sending it from here makes the header
 * mean what the portal is actually rendering in, and leaves the mobile app's use of the same
 * endpoint untouched.
 *
 * It reads i18next's singleton rather than `config/i18n`, which would close an import cycle back
 * into this module (config/i18n -> languageUtils -> translationsService -> api).
 *
 * **Only when the user has actually chosen a language.** i18next falls back to en-GB when no
 * preference is stored, and sending that would overwrite the browser's own Accept-Language for
 * every user who never touched the setting. Server-side the header wins
 * (ServiceContext.GetLanguageCodeAsync) and drives survey texts, medication and measurement
 * names and PROM details, so an HCP on a de-DE browser with no stored preference would silently
 * lose content they used to get in their own language — and could not get it back, because the
 * Settings picker only offers languages with Portal rows. Staying quiet leaves those users
 * exactly as they were and still lets an explicit choice reach the API.
 */
export function attachLanguage<T extends InternalAxiosRequestConfig>(
  request: T
): T {
  const chosenLanguage = readRequestedLanguage();

  if (
    chosenLanguage &&
    request.headers &&
    !request.headers["Accept-Language"]
  ) {
    // i18next's resolved tag, not the raw stored one, so the header matches the bundle the
    // portal is actually rendering.
    request.headers["Accept-Language"] = i18next.language ?? chosenLanguage;
  }

  return request;
}

function getRegionalApiUrl(region?: string): string {
  if (region?.toUpperCase() === "US" && process.env.REACT_APP_US_API_URL) {
    return process.env.REACT_APP_US_API_URL;
  }
  return DEFAULT_API_URL!;
}

function initApiBaseUrl(): string {
  const storedUrl = safeStorage.getItem(AUTH_API_BASE_URL);
  return storedUrl || DEFAULT_API_URL!;
}

export const api = axios.create({
  baseURL: initApiBaseUrl(),
  headers: {
    "Content-Type": "application/json",
    TIMEZONE: Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
});

// Bare axios instance for direct uploads to S3 via presigned URLs. No
// interceptors — sending our Bearer token to S3 would break the signed
// request, and we want native axios errors so the upload caller can
// distinguish "API said no" from "no response received".
export const s3Axios = axios.create({ timeout: 10 * 60 * 1000 });

async function isApiHealthy(
  apiUrl: string,
  timeoutMs = 3000
): Promise<boolean> {
  try {
    const baseUrl = apiUrl.replace(/\/v1\/?$/, "");
    const response = await axios.get(`${baseUrl}/health`, {
      timeout: timeoutMs,
    });
    return response.status >= 200 && response.status < 300;
  } catch {
    return false;
  }
}

export async function setApiBaseUrlForRegion(region?: string): Promise<void> {
  const regionalUrl = getRegionalApiUrl(region);

  if (regionalUrl !== DEFAULT_API_URL) {
    const healthy = await isApiHealthy(regionalUrl);
    if (!healthy) {
      api.defaults.baseURL = DEFAULT_API_URL!;
      safeStorage.setItem(AUTH_API_BASE_URL, DEFAULT_API_URL!);
      return;
    }
  }

  api.defaults.baseURL = regionalUrl;
  safeStorage.setItem(AUTH_API_BASE_URL, regionalUrl);
}

export const checkIfUserIsAuthenticated = async ({
  status,
}: {
  status: number;
}) => {
  const isUserUnauthenticated = status === 401;

  const tokenResponse = safeStorage.getItem(AUTH_TOKEN_STORAGE);

  if (tokenResponse !== null && isUserUnauthenticated) {
    const parsedTokenResponse = safeJsonParse<IToken>(
      tokenResponse,
      {} as IToken
    );

    try {
      const refreshTokenResponse = await authService.refreshToken(
        parsedTokenResponse.refreshToken
      );

      if (refreshTokenResponse.status !== 400) {
        parsedTokenResponse.idToken = refreshTokenResponse.data.idToken;

        safeStorage.setItem(
          AUTH_TOKEN_STORAGE,
          JSON.stringify(parsedTokenResponse)
        );

        api.defaults.headers.common.Authorization = `${parsedTokenResponse.tokenType} ${parsedTokenResponse.idToken}`;

        return true;
      }
    } catch (error) {
      console.error("Error refreshing token", error);
      return false;
    }
  }

  if (isUserUnauthenticated) {
    safeStorage.clear();
    window.location.assign("/login");
    return false;
  }

  return false;
};

api.interceptors.request.use(attachStoredToken);
api.interceptors.request.use(attachLanguage);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest.retry &&
      !originalRequest.anonymous
    ) {
      originalRequest.retry = true;

      const tokenRefreshed = await checkIfUserIsAuthenticated({
        status: error.response.status,
      });

      if (tokenRefreshed) {
        const tokenResponse = safeStorage.getItem(AUTH_TOKEN_STORAGE);

        if (tokenResponse) {
          const parsedTokenResponse = safeJsonParse<IToken>(
            tokenResponse,
            {} as IToken
          );

          originalRequest.headers.Authorization = `${parsedTokenResponse.tokenType} ${parsedTokenResponse.idToken}`;

          return api(originalRequest);
        }
      }
    }

    return error.response;
  }
);
